'use client';
import React from 'react';
import {
  deleteFromLocalStorage,
  isNullOrUndefined,
  setStringLocalStorage,
} from '@r-and-a-shared-ui/utils';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import {
  federatedSignIn,
  federatedSignOut,
  signUp,
  handleOauthSignedIn,
  handleOauthSignedOut,
  updateAccountData,
  upgradeAccount,
  changePassword,
  deleteAccount,
  fetchTokens,
} from './authProviderMethods';
import { Attributes, CognitoUser, initialMembership } from '../types';
import {
  getHighestMembership,
  tryGetRefreshToken,
  getMembershipStartingDate,
  USER_ID,
  getUserReferralCode,
  REFERRAL_CODE,
} from '../utils';
import { fetchUserAttributes } from 'aws-amplify/auth';

export interface AuthProviderProps {
  children?: ReactNode;
  awsExports?: ResourcesConfig;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  awsExports,
}) => {
  if (!isNullOrUndefined(awsExports)) {
    Amplify.configure(awsExports, { ssr: true });
  }

  const config = awsExports!.Auth!;

  const [user, setUser] = useState<CognitoUser | undefined>(undefined);
  const [membership, setMembership] = useState(initialMembership);
  const [referralCode, setReferralCode] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [refreshToken, setRefreshToken] = useState<string | undefined>();
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [idToken, setIdToken] = useState<string | undefined>();

  const setAuthState = async (cognitoUser: CognitoUser) => {
    setUser(cognitoUser);
    setMembership(getHighestMembership(cognitoUser));
    setRefreshToken(await tryGetRefreshToken());
    const tokens = await fetchTokens();
    setAccessToken(tokens?.accessToken?.toString());
    setIdToken(tokens?.idToken?.toString());

    if (cognitoUser && cognitoUser[Attributes.SsoUserId]) {
      const userId = String(cognitoUser[Attributes.SsoUserId]);
      setStringLocalStorage(USER_ID, userId);
    }
  };

  const resetAuthState = () => {
    setUser(undefined);
    setMembership(initialMembership);
    setIsAuthenticated(false);
    deleteFromLocalStorage(USER_ID);
    deleteFromLocalStorage(REFERRAL_CODE);
  };

  const checkAuthStatus = useCallback(async () => {
    await fetchUserAttributes()
      .then(user => {
        const mappedUser = user as CognitoUser;
        setAuthState(mappedUser);
      })
      .catch(e => {
        resetAuthState();
      })
      .finally(() => {
        setHasCheckedAuth(true);
      });
  }, []);

  const membershipStartedAtCallback = useCallback(async () => {
    if (!user) {
      return;
    }
    getMembershipStartingDate(user, membership.type, config).then(
      (response): void => {
        setMembership({ ...membership, startedAt: response });
      },
    );
  }, [user, config, membership]);

  const referralCodeCallback = useCallback(async () => {
    if (!user) {
      return;
    }
    getUserReferralCode(user, config).then(
      (response): void => {
        setReferralCode(response);
      },
    );
  }, [user, config])

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (!isNullOrUndefined(accessToken) && !isNullOrUndefined(idToken)) {
      setIsAuthenticated(true);
    }
  }, [accessToken, idToken]);

  useEffect(() => {
    membershipStartedAtCallback();
    referralCodeCallback();
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        methods: {
          federatedSignIn,
          federatedSignOut,
          signUp,
          updateAccountData,
          upgradeAccount,
          handleOauthSignedIn,
          handleOauthSignedOut,
          changePassword,
          deleteAccount,
        },
        userState: {
          user,
          membership,
          referralCode,
          isAuthenticated,
          refreshToken,
          accessToken,
          idToken,
          checkAuthStatus,
          hasCheckedAuth,
        },
        config,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
